import { ChevronDown, Octagon, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom-v5-compat';

import { Input } from '../components/Input';
import { useIsAdmin } from '../hooks/useCurrentUser';
import { useMobileApp } from '../hooks/useMobileApp';
import { cn } from '../lib/cn';
import { useAppSelector } from './hooks';
import { MOBILE_FULL_ACCESS, mobileNavGroups, navGroups, navGroupsForRole, type NavBadge, type NavGroup, type NavItem } from './nav';

const badgeStyles: Record<NavBadge, string> = {
    WIP: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
    BETA: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
};

const navItemBase = 'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors';
const navItemActive = 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
const navItemIdle =
    'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100';

function NavItemBody({ item }: { item: NavItem }) {
    const Icon = item.icon;
    return (
        <>
            <span aria-hidden="true" className="flex h-5 w-5 shrink-0 items-center justify-center">
                <Icon className="h-5 w-5" />
            </span>
            <span className="truncate">{item.label}</span>
            {item.badge && (
                <span
                    className={cn(
                        'ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                        badgeStyles[item.badge],
                    )}
                >
                    {item.badge === 'WIP' && <Octagon aria-hidden="true" className="h-3 w-3 shrink-0" />}
                    {item.badge}
                </span>
            )}
        </>
    );
}

function NavItemLink({ item, onNavigate }: { item: NavItem; onNavigate: () => void }) {
    // Legacy (v1) destinations live outside the v2 router subtree — a full-page
    // anchor boots the legacy shell cleanly instead of client-navigating across
    // the router boundary. They're never "active" within the v2 router.
    if (item.external) {
        return (
            <a href={item.to} onClick={onNavigate} className={cn(navItemBase, navItemIdle)}>
                <NavItemBody item={item} />
            </a>
        );
    }
    return (
        <NavLink
            to={item.to}
            onClick={onNavigate}
            className={({ isActive }) => cn(navItemBase, isActive ? navItemActive : navItemIdle)}
        >
            <NavItemBody item={item} />
        </NavLink>
    );
}

function filterGroups(groups: NavGroup[], rawQuery: string): NavGroup[] {
    const q = rawQuery.trim().toLowerCase();
    if (!q) return groups;
    const out: NavGroup[] = [];
    for (const g of groups) {
        const groupHit = g.label.toLowerCase().includes(q);
        const items = groupHit ? g.items : g.items.filter((i) => i.label.toLowerCase().includes(q));
        if (items.length > 0) out.push({ ...g, items });
    }
    return out;
}

interface SidebarProps {
    isOpen: boolean;
    onNavigate: () => void;
    /**
     * `shell` (default): drawer below `lg`, permanent static rail at `lg+`
     * (the main app layout). `overlay`: always a slide-in drawer with a
     * backdrop at every breakpoint — used by the chromeless full-screen
     * surfaces (Tickete) that have no permanent rail of their own.
     */
    variant?: 'shell' | 'overlay';
    /**
     * Desktop-collapsed: drop the permanent `lg+` rail so the main content gets
     * full width. The mobile drawer (`isOpen`) is unaffected. Ignored for
     * `overlay` (which never has a permanent rail).
     */
    collapsed?: boolean;
}

export function Sidebar({ isOpen, onNavigate, variant = 'shell', collapsed = false }: SidebarProps) {
    const isOverlay = variant === 'overlay';
    const [search, setSearch] = useState('');
    const location = useLocation();

    const isMobileApp = useMobileApp();
    const isAdmin = useIsAdmin();
    const isShop = useAppSelector(
        (state) => (state as any).authentication?.user?.role?.name === 'Shop',
    );
    // Mobile PWA: MOBILE_FULL_ACCESS opens the whole menu; otherwise non-admins
    // get only the mobile-enabled menus (Tickete + Rapoarte zilnice) while admins
    // keep the full menu. On desktop, shop-kiosk accounts get the trimmed menu;
    // everyone else gets everything.
    const groups = useMemo(() => {
        if (isMobileApp) {
            if (MOBILE_FULL_ACCESS) return navGroups;
            return isAdmin ? navGroups : mobileNavGroups;
        }
        return navGroupsForRole(navGroups, isShop);
    }, [isMobileApp, isAdmin, isShop]);

    const activeGroupIds = useMemo(() => {
        const ids = new Set<string>();
        for (const g of groups) {
            if (g.items.some((i) => location.pathname === i.to || location.pathname.startsWith(i.to + '/'))) {
                ids.add(g.id);
            }
        }
        return ids;
    }, [location.pathname, groups]);

    const [openIds, setOpenIds] = useState<Set<string>>(() => new Set(activeGroupIds));

    // Ensure the group containing the active route is expanded after navigation.
    // We additively merge so groups the user opened by hand stay open.
    useEffect(() => {
        setOpenIds((prev) => {
            let changed = false;
            const next = new Set(prev);
            for (const id of activeGroupIds) {
                if (!next.has(id)) {
                    next.add(id);
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [activeGroupIds]);

    const toggleGroup = (id: string) => {
        setOpenIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const filteredGroups = useMemo(() => filterGroups(groups, search), [groups, search]);
    const searchActive = search.trim().length > 0;

    return (
        <>
            <div
                className={cn(
                    'fixed inset-0 z-30 bg-black/40 transition-opacity',
                    !isOverlay && 'lg:hidden',
                    isOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
                )}
                onClick={onNavigate}
                aria-hidden="true"
            />
            <aside
                className={cn(
                    'fixed inset-y-0 left-0 z-40 flex w-64 transform flex-col border-r border-solid border-gray-200 bg-white transition-transform dark:border-gray-700 dark:bg-gray-800',
                    // Permanent rail at lg+ only when not collapsed; collapsed
                    // leaves it as an off-canvas drawer so main content fills.
                    !isOverlay && !collapsed && 'lg:static lg:translate-x-0',
                    isOpen ? 'translate-x-0' : '-translate-x-full',
                )}
                aria-label="Navigare principala"
            >
                <div className="flex h-14 items-center gap-2 border-b border-solid border-gray-200 px-4 dark:border-gray-700">
                    <img src="/logo192.png" alt="" className="h-7 w-7 shrink-0" />
                    <span className="whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-gray-100">KRIT Orchestrator</span>
                    <span className="shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                        v2
                    </span>
                </div>
                <div className="border-b border-solid border-gray-200 p-3 dark:border-gray-700">
                    <Input
                        type="search"
                        icon={Search}
                        placeholder="Cauta in meniu..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        aria-label="Cauta in meniu"
                    />
                </div>
                <nav className="flex-1 overflow-y-auto p-2">
                    {filteredGroups.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">Niciun rezultat.</p>
                    ) : (
                        <ul className="flex flex-col gap-1">
                            {filteredGroups.map((group) => {
                                const GroupIcon = group.icon;
                                if (group.flat) {
                                    const item = group.items[0];
                                    if (!item) return null;
                                    return (
                                        <li key={group.id}>
                                            <NavItemLink item={item} onNavigate={onNavigate} />
                                        </li>
                                    );
                                }
                                const expanded = searchActive || openIds.has(group.id);
                                return (
                                    <li key={group.id}>
                                        <button
                                            type="button"
                                            onClick={() => toggleGroup(group.id)}
                                            aria-expanded={expanded}
                                            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                                        >
                                            <span aria-hidden="true" className="flex h-5 w-5 shrink-0 items-center justify-center">
                                                <GroupIcon className="h-4 w-4" />
                                            </span>
                                            <span className="truncate">{group.label}</span>
                                            <ChevronDown
                                                aria-hidden="true"
                                                className={cn(
                                                    'ml-auto h-4 w-4 shrink-0 transition-transform',
                                                    expanded && 'rotate-180',
                                                )}
                                            />
                                        </button>
                                        {expanded && (
                                            <ul className="ml-3 mt-1 flex flex-col gap-1 border-l border-solid border-gray-200 pl-2 dark:border-gray-700">
                                                {group.items.map((item) => (
                                                    <li key={item.to}>
                                                        <NavItemLink item={item} onNavigate={onNavigate} />
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </nav>
                <div className="border-t border-solid border-gray-200 p-3 text-xs text-gray-400 dark:border-gray-700 dark:text-gray-500">
                    Versiunea noua a aplicatiei.
                </div>
            </aside>
        </>
    );
}
